/**
 * Product Title Formatter
 * Converts ALL-CAPS product titles to Title Case with bold brand names.
 * Brand detection via longest-prefix match against known ammo brands.
 */
(function () {
  'use strict';

  // Brand lookup: { lowercase_alias: 'Display Name' }
  var BRANDS = {};
  var entries = [
    ['Aguila'],
    ['Ammo Inc.', 'Ammo Inc'],
    ['Barnes'],
    ['CCI', 'CCI Ammunition', 'CCi'],
    ['Federal'],
    ['Fiocchi', 'Fiocchi Ammunition'],
    ['Hornady'],
    ['ICC'],
    ['Norma'],
    ['PMC', 'PMC Ammunition'],
    ['Remington', 'Remington Ammunition'],
    ['Ruag'],
    ['Sellier & Bellot', 'S&B'],
    ['Sig Sauer'],
    ['Sterling'],
    ['TulAmmo'],
    ['Winchester']
  ];
  entries.forEach(function (group) {
    var canonical = group[0];
    group.forEach(function (alias) {
      BRANDS[alias.toLowerCase()] = canonical;
    });
  });

  // Sorted alias keys longest-first for greedy prefix matching
  var BRAND_KEYS = Object.keys(BRANDS).sort(function (a, b) {
    return b.length - a.length;
  });

  // Known all-caps abbreviations in ammo titles
  var ABBREVIATIONS = [
    'FMJ', 'JHP', 'ACP', 'NATO', 'LR', 'WMR', 'OTM', 'HP', 'SP',
    'TMC', 'JSP', 'XTP', 'HST', 'UMC', 'USA', 'BTHP', 'VNT', 'MFX',
    'XPB', 'SXT', 'BHP', 'CPD', 'TSX', 'TTSX', 'GMX', 'SST', 'ELD',
    'CHP', 'FTX', 'SWC', 'LSWC', 'LRN', 'RN', 'WC', 'SJHP', 'SJSP',
    'GAP', 'AUTO', 'MAG', 'WIN', 'REM', 'SPR', 'GOVT'
  ];
  var ABBR_SET = {};
  ABBREVIATIONS.forEach(function (a) { ABBR_SET[a] = true; });

  // Small words that stay lowercase (unless first word)
  var SMALL_WORDS = { of: 1, per: 1, and: 1, the: 1, in: 1, for: 1, a: 1, an: 1, or: 1, to: 1 };

  // Caliber pattern: optional dot, digits, optional decimal, optional x-dimension, optional unit
  var CALIBER_RE = /^\.?\d+(\.\d+)?(x\d+)?(mm|ga)?$/i;

  // Number+unit pattern: 124gr, 50rd, 20ct, 25pk
  var NUM_UNIT_RE = /^\d+(gr|grain|rd|ct|pk)$/i;

  /**
   * Convert a single word to Title Case with ammo-aware exceptions.
   */
  function titleCaseWord(word, isFirst) {
    // Strip leading punctuation (quotes, brackets, parens)
    var leadMatch = word.match(/^([""''"\'\(\[\{]+)(.+)$/);
    var lead = leadMatch ? leadMatch[1] : '';
    var afterLead = leadMatch ? leadMatch[2] : word;

    // Strip trailing punctuation for abbreviation check
    var punctMatch = afterLead.match(/^(.+?)([.,;:!?""''"\'\)\]\}]+)$/);
    var core = punctMatch ? punctMatch[1] : afterLead;
    var punct = punctMatch ? punctMatch[2] : '';

    // Check abbreviation (case-insensitive)
    if (ABBR_SET[core.toUpperCase()]) {
      return lead + core.toUpperCase() + punct;
    }

    // Caliber pattern — preserve as-is (lowercase the unit)
    if (CALIBER_RE.test(core)) {
      return lead + core.replace(/mm|ga/gi, function (m) { return m.toLowerCase(); }) + punct;
    }

    // Number+unit — lowercase
    if (NUM_UNIT_RE.test(core)) {
      return lead + core.toLowerCase() + punct;
    }

    // Small words — lowercase unless first word or starts with leading punct
    if (!isFirst && !lead && SMALL_WORDS[core.toLowerCase()]) {
      return core.toLowerCase() + punct;
    }

    // Hyphenated words ("Laser-Engraved") — title-case each side
    if (/^[A-Za-z]+(-[A-Za-z]+)+$/.test(core)) {
      return lead + core.split('-').map(function (part) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }).join('-') + punct;
    }

    // Words with "&" — title-case each side
    if (core.indexOf('&') !== -1) {
      return lead + core.split('&').map(function (part, i) {
        return titleCaseWord(part, i === 0 && isFirst);
      }).join('&') + punct;
    }

    // Default: capitalize first letter, lowercase rest
    return lead + core.charAt(0).toUpperCase() + core.slice(1).toLowerCase() + punct;
  }

  /**
   * Convert full title string to Title Case.
   * Preserves casing within quoted phrases (e.g. "We The People").
   */
  function toTitleCase(str) {
    // Split into quoted segments and unquoted segments
    // Matches: "...", \u201C...\u201D (smart quotes), '...', \u2018...\u2019
    // Double quotes only: an apostrophe ("Don't") is not a quote mark.
    var parts = str.split(/(["\u201C\u201D][^"\u201C\u201D]+["\u201C\u201D])/);
    return parts.map(function (part) {
      // If this part is a quoted phrase, title-case each word but treat
      // all words as "first" so small words stay capitalized
      if (/^["\u201C\u201D]/.test(part) && /["\u201C\u201D]$/.test(part)) {
        return part.split(/\s+/).map(function (word) {
          if (!word) return word;
          return titleCaseWord(word, true);
        }).join(' ');
      }
      // Normal unquoted segment
      return part.split(/\s+/).map(function (word, i) {
        if (!word) return word;
        return titleCaseWord(word, i === 0);
      }).join(' ');
    }).join('');
  }

  /**
   * Detect brand via longest-prefix match against known aliases.
   * Returns { canonical: string, length: number } or null.
   */
  function detectBrand(titleLower) {
    for (var i = 0; i < BRAND_KEYS.length; i++) {
      var key = BRAND_KEYS[i];
      if (titleLower.indexOf(key) === 0) {
        // Ensure the match ends at a word boundary (space or end-of-string)
        var nextChar = titleLower.charAt(key.length);
        if (nextChar === '' || nextChar === ' ') {
          return { canonical: BRANDS[key], length: key.length };
        }
      }
    }
    return null;
  }

  /**
   * Create a span element with given class and text content.
   */
  function makeSpan(className, text) {
    var span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }

  /**
   * Format a product title h1 element.
   * Uses DOM methods (not innerHTML) for safe content insertion.
   */
  function formatProductTitle(h1) {
    if (h1.hasAttribute('data-title-format-skip')) return;

    var rawTitle = h1.getAttribute('data-raw-title');
    if (!rawTitle) return;

    var titleLower = rawTitle.toLowerCase().trim();
    var brand = detectBrand(titleLower);

    // Clear existing content
    while (h1.firstChild) {
      h1.removeChild(h1.firstChild);
    }

    if (brand) {
      h1.appendChild(makeSpan('product-title__brand', brand.canonical));
      var rest = rawTitle.substring(brand.length).trim();
      if (rest) {
        h1.appendChild(document.createTextNode(' '));
        h1.appendChild(makeSpan('product-title__detail', toTitleCase(rest)));
      }
    } else {
      h1.appendChild(makeSpan('product-title__detail', toTitleCase(rawTitle)));
    }

    h1.setAttribute('data-formatted', 'true');
    h1.classList.remove('product__heading--unformatted');
  }

  /**
   * Find and format all product title headings on the page.
   */
  function formatAll() {
    var headings = document.querySelectorAll('.product__heading[data-raw-title]:not([data-formatted])');
    for (var i = 0; i < headings.length; i++) {
      formatProductTitle(headings[i]);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', formatAll);
  } else {
    formatAll();
  }

  // Re-run on product swaps (Shopify section re-render)
  document.addEventListener('product-info:loaded', function () {
    requestAnimationFrame(formatAll);
  });
})();
